/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import {
	Award,
	BadgeCheck,
	FileCheck2,
	Factory,
	Globe2,
	HeartPulse,
	Layers3,
	Leaf,
	ShieldCheck,
	Stamp,
	Waves,
	Zap,
	type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Navigation } from "@/components/customCopmonent/landingSection/navigation";
import { FooterSection } from "@/components/customCopmonent/landingSection/footer-section";
import {
	Card,
	PageHeader,
	PageShell,
	RevealOnScroll,
	SectionDivider,
	SectionWrapper,
} from "./OurStory.page-primitives";
import DotGrid from "@/components/customCopmonent/DotGrid";
import { motion } from "motion/react";
import FlowingMenu from "@/components/customCopmonent/FlowingMenu";
import { PageHeaderHero, PAGE_HERO_CLASS } from "@/components/customCopmonent/Text/PageHeaderHero";
import Seo from "@/components/Seo";

/* Non-translatable assets, kept in fixed order; text resolves from the locale. */
const bgimage = "/images/header/OURSTORY-1.JPG";

const footprintMeta: { image: string; icon: LucideIcon }[] = [
	{
		image: "/images/industriesPhotos/Oil&Gas/SubIndustries/midstream.jpg",
		icon: Zap,
	},
	{
		image:
			"/images/industriesPhotos/RenewableEnergy/SubIndustries/waste-water-Energy.jpg",
		icon: Waves,
	},
	{ image: "/images/freeDownloads/data-centers.jpg", icon: Globe2 },
];

const standardsImages = [
	"/images/freeDownloads/TheGoldStandardofManufacturing.jpg",
	"/images/freeDownloads/MarketAccessRegulatoryExcellence.jpg",
	"/images/freeDownloads/IntegratedManagementSystem.jpg",
];

const trustedBy = [
		{
			link: "#",
			text: "Saudi Aramco",
			image: "/images/logos/logoslider/saudiaramco.png",
		},
		{
			link: "#",
			text: "ADNOC",
			image: "/images/logos/logoslider/adnoc.png",
		},
		{
			link: "#",
			text: "Petroleum Development Oman (PDO)",
			image: "/images/logos/logoslider/pdo.png",
		},
		{
			link: "#",
			text: "Hitachi",
			image: "/images/logos/logoslider/hitachi.png",
		},
		{
			link: "#",
			text: "GE",
			image: "/images/logos/logoslider/ge.png",
		},
		{
			link: "#",
			text: "Toshiba",
			image: "/images/logos/logoslider/thoshiba.png",
		},
		{
			link: "#",
			text: "NTPC",
			image: "/images/logos/logoslider/National_Thermal_Power_logo.svg.png",
		},
		{
			link: "#",
			text: "NHPC ",
			image: "/images/logos/logoslider/NHPC_official_logo.svg.png",
		},
		{
			link: "#",
			text: "TOTALENERGIES",
			image: "/images/logos/logoslider/TotalEnergies_logo.svg.png",
		},
	];

const highlightClass = "text-md font-semibold text-slate-700";

const renderIntroduction = (text: string) =>
	text.split(/(\*\*.*?\*\*)/g).map((part, index) => {
		const isHighlighted = part.startsWith("**") && part.endsWith("**");
		const content = isHighlighted ? part.slice(2, -2) : part;

		if (!content) {
			return null;
		}

		return isHighlighted ?
				<span
					key={`${content}-${index}`}
					className={highlightClass}>
					{content}
				</span>
			:	<span key={`${content}-${index}`}>{content}</span>;
	});

/* ------------------------------------------------------------------ */
/*  Per-item icon + colour mapping for the standards cards             */
/* ------------------------------------------------------------------ */
interface ItemMeta {
	Icon: LucideIcon;
	bg: string;
	fg: string;
	ring: string;
	image?: string;
}

const ITEM_FALLBACK: ItemMeta = {
	Icon: BadgeCheck,
	bg: "bg-slate-100",
	fg: "text-slate-700",
	ring: "ring-slate-200",
};

/* ------------------------------------------------------------------ */
/*  Per-card theme â€” different colour per standard category            */
/* ------------------------------------------------------------------ */
interface CardTheme {
	iconBg: string;
	iconShadow: string;
	cornerGlow: string;
	ghostText: string;
	cardBorderHover: string;
	cardShadowHover: string;
	sweepBar: string;
}

const STANDARD_THEMES: CardTheme[] = [
	// 0 â€” Gold Standard of Manufacturing (amber / gold)
	{
		iconBg: "linear-gradient(135deg, #b45309 0%, #f59e0b 100%)",
		iconShadow: "shadow-[0_16px_36px_rgba(180,83,9,0.4)]",
		cornerGlow: "rgba(245,158,11,0.28)",
		ghostText: "text-amber-700/[0.05] group-hover:text-amber-700/[0.08]",
		cardBorderHover: "group-hover:border-amber-500/30",
		cardShadowHover: "group-hover:shadow-[0_24px_60px_rgba(180,83,9,0.22)]",
		sweepBar: "linear-gradient(90deg, #b45309 0%, #f59e0b 100%)",
	},
	// 1 â€” Market Access & Regulatory Excellence (blue / global)
	{
		iconBg: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
		iconShadow: "shadow-[0_16px_36px_rgba(30,64,175,0.4)]",
		cornerGlow: "rgba(59,130,246,0.28)",
		ghostText: "text-blue-700/[0.05] group-hover:text-blue-700/[0.08]",
		cardBorderHover: "group-hover:border-blue-500/30",
		cardShadowHover: "group-hover:shadow-[0_24px_60px_rgba(30,64,175,0.22)]",
		sweepBar: "linear-gradient(90deg, #1e40af 0%, #3b82f6 100%)",
	},
	// 2 â€” Integrated Management System (emerald / sustainability)
	{
		iconBg: "linear-gradient(135deg, #047857 0%, #10b981 100%)",
		iconShadow: "shadow-[0_16px_36px_rgba(4,120,87,0.4)]",
		cornerGlow: "rgba(16,185,129,0.28)",
		ghostText: "text-emerald-700/[0.05] group-hover:text-emerald-700/[0.08]",
		cardBorderHover: "group-hover:border-emerald-500/30",
		cardShadowHover: "group-hover:shadow-[0_24px_60px_rgba(4,120,87,0.22)]",
		sweepBar: "linear-gradient(90deg, #047857 0%, #10b981 100%)",
	},
];

const getItemMeta = (text: string): ItemMeta => {
	const t = text.toLowerCase();
	if (
		t.includes("asme 'nb'") ||
		t.includes("national board") ||
		t.includes("(nb)")
	)
		return {
			Icon: FileCheck2,
			bg: "bg-indigo-100",
			fg: "text-indigo-700",
			ring: "ring-indigo-200",
		};
	if (t.includes("asme"))
		return {
			Icon: Stamp,
			bg: "bg-blue-100",
			fg: "text-blue-700",
			ring: "ring-blue-200",
		};
	if (
		t.includes("ce-ped") ||
		t.includes("ce ped") ||
		t.includes("ukca") ||
		t.includes("dosh") ||
		t.includes("crn") ||
		t.includes("eac") ||
		t.includes("nr13")
	)
		return {
			Icon: Globe2,
			bg: "bg-amber-100",
			fg: "text-amber-700",
			ring: "ring-amber-200",
		};
	if (t.includes("iso 9001") || t.includes("quality management"))
		return {
			Icon: Award,
			bg: "bg-yellow-100",
			fg: "text-yellow-700",
			ring: "ring-yellow-200",
			image: "/images/logos/iso/iso-9001.png",
		};
	if (t.includes("iso 14001") || t.includes("environment"))
		return {
			Icon: Leaf,
			bg: "bg-emerald-100",
			fg: "text-emerald-700",
			ring: "ring-emerald-200",
			image: "/images/logos/iso/iso-14001.webp",
		};
	if (
		t.includes("iso 45001") ||
		t.includes("occupational") ||
		t.includes("health") ||
		t.includes("safety")
	)
		return {
			Icon: HeartPulse,
			bg: "bg-rose-100",
			fg: "text-rose-700",
			ring: "ring-rose-200",
			image: "/images/logos/iso/iso-45001.png",
		};
	return ITEM_FALLBACK;
};

const OurStory = () => {
	const { t } = useTranslation();

	const meta = t("aboutPages.ourStory.meta", {
		returnObjects: true,
	}) as string[];

	const footprint = (
		t("aboutPages.ourStory.footprint", {
			returnObjects: true,
		}) as { title: string; description: string }[]
	).map((item, index) => ({
		...item,
		image: footprintMeta[index]?.image ?? "",
		icon: footprintMeta[index]?.icon ?? Globe2,
	}));

	const standards = (
		t("aboutPages.ourStory.standards", {
			returnObjects: true,
		}) as { title: string; items: { code: string; description: string }[] }[]
	).map((section, index) => ({
		...section,
		image: standardsImages[index] ?? "",
	}));

	return (
		<PageShell>
			<Seo
				titleKey="aboutPages.ourStory.seo.title"
				descriptionKey="aboutPages.ourStory.seo.description"
				path="/our-story"
			/>
			<Navigation />
			<SectionWrapper className={PAGE_HERO_CLASS}>
				<div className="absolute inset-0">
					<div
						className="absolute bottom-3 inset-0 opacity-35 scale-105"
						style={{
							backgroundImage: `url(${bgimage})`,
							backgroundSize: "cover",
							backgroundPosition: "center",
						}}
					/>
					<DotGrid
						dotSize={4}
						gap={15}
						baseColor="#3b0092"
						activeColor="#f09a38"
						proximity={100}
						shockRadius={200}
						shockStrength={3}
						resistance={750}
						returnDuration={1.5}
					/>
				</div>
				<PageHeaderHero
					// align="center"
					title={t("aboutPages.ourStory.title")}
					description={t("aboutPages.ourStory.tagline")}
					meta={meta}
				/>
			</SectionWrapper>
			<SectionDivider />
			<SectionWrapper className="pt-0">
				<RevealOnScroll>
					<Card className="overflow-hidden border-[#2d2f92]/10 bg-gradient-to-br from-[#2d2f92]/5 via-white to-[#f9a93a]/10">
						<div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#2d2f92]/15 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2d2f92]">
							<Factory className="h-4 w-4" />
							{t("aboutPages.ourStory.introductionLabel")}
						</div>

						<p className="text-base leading-8 text-slate-700 sm:text-lg sm:leading-9">
							{renderIntroduction(t("aboutPages.ourStory.introduction"))}
						</p>
					</Card>
				</RevealOnScroll>
			</SectionWrapper>
			<SectionDivider />

			<SectionWrapper
				className="pt-0  !px-0 !max-w-none"
				contentClassName="!max-w-none !px-0">
				{" "}
				<div className="relative flex flex-col items-center justify-center ">
					{/* ===== HEADER ===== */}
					<RevealOnScroll>
						<PageHeader
							eyebrow={t("aboutPages.ourStory.footprintHeader.eyebrow")}
							title={t("aboutPages.ourStory.footprintHeader.title")}
							description={t(
								"aboutPages.ourStory.footprintHeader.description",
							)}
							align="center"
							className="py-6 text-center items-center "
						/>
					</RevealOnScroll>

					{/* =====
						ASYMMETRIC GRID
						- Col 1: card 0 spans 2 rows (tall feature card)
						- Col 2: cards 1 & 2 stacked
					===== */}
					<div className="relative grid w-full max-w-6xl gap-6 px-6 md:grid-cols-2 md:grid-rows-2">
						{footprint.map((item, index) => {
							const Icon = item.icon;
							const isTall = index === 0;

							return (
								<motion.div
									key={item.title}
									initial={{ opacity: 0, y: 60, scale: 0.95 }}
									whileInView={{ opacity: 1, y: 0, scale: 1 }}
									viewport={{ once: true, margin: "-50px" }}
									whileHover={{ scale: 1.02 }}
									transition={{
										duration: 0.6,
										delay: index * 0.1,
										ease: [0.22, 1, 0.36, 1],
									}}
									className={`group relative w-full overflow-hidden rounded-2xl cursor-pointer ${
										isTall ?
											"md:row-span-2 min-h-[420px] md:min-h-[640px]"
										:	"min-h-[300px] md:min-h-[310px]"
									}`}>
									{/* ===== IMAGE BACKGROUND ===== */}
									<motion.img
										src={item.image}
										alt={item.title}
										className="absolute inset-0 w-full h-full object-cover"
										whileHover={{ scale: 1.12 }}
										transition={{ duration: 0.7 }}
									/>

									{/* ===== DARK OVERLAY ===== */}
									<div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/45 to-black/85" />

									{/* ===== GLOW EFFECT ===== */}
									<div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 bg-[radial-gradient(circle_at_center,rgba(249,169,58,0.25),transparent_70%)]" />

									{/* ===== FEATURED TAG (tall card only) ===== */}
									{isTall && (
										<div className="absolute top-5 left-5 z-20 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-white backdrop-blur-md">
											<span
												className="h-1.5 w-1.5 rounded-full"
												style={{ background: "#f9a93a" }}
											/>
											{t("aboutPages.ourStory.footprintHeader.flagshipTag")}
										</div>
									)}

									{/* ===== INDEX (small cards only) ===== */}
									{!isTall && (
										<span className="absolute top-5 left-5 z-20 font-mono text-[11px] font-semibold tracking-[0.22em] text-white/60">
											{String(index + 1).padStart(2, "0")} /{" "}
											{String(footprint.length).padStart(2, "0")}
										</span>
									)}

									{/* ===== CONTENT ===== */}
									<div
										className={`relative z-10 flex h-full flex-col justify-end text-white ${
											isTall ? "p-8 sm:p-10" : "p-6"
										}`}>
										{/* ICON */}
										<div
											className={`mb-4 flex items-center justify-center rounded-xl bg-white/10 backdrop-blur-md border border-white/20 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3 ${
												isTall ? "h-14 w-14" : "h-12 w-12"
											}`}>
											<Icon
												className={`text-[#f9a93a] ${
													isTall ? "h-6 w-6" : "h-5 w-5"
												}`}
											/>
										</div>

										{/* TITLE */}
										<h3
											className={`font-semibold tracking-tight ${
												isTall ?
													"text-2xl sm:text-3xl leading-[1.15]"
												:	"text-lg leading-tight"
											}`}>
											{item.title}
										</h3>

										{/* DESCRIPTION */}
										<p
											className={`mt-2 text-white/85 ${
												isTall ?
													"text-base leading-relaxed max-w-md"
												:	"text-sm leading-6"
											}`}>
											{item.description}
										</p>

										{/* HOVER LINE */}
										<div
											className={`mt-4 h-[2px] w-0 bg-gradient-to-r from-[#f9a93a] to-[#2d2f92] transition-all duration-500 ${
												isTall ? "group-hover:w-28" : "group-hover:w-16"
											}`}
										/>

										{/* ARROW (tall card only) */}
										{isTall && (
											<div className="mt-6 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/70 transition-colors duration-300 group-hover:text-[#f9a93a]">
												{t("aboutPages.ourStory.footprintHeader.exploreSector")}
												<span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-white/20 transition-all duration-500 group-hover:translate-x-1 group-hover:border-[#f9a93a]/60">
													<svg
														className="h-3.5 w-3.5"
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														strokeWidth="2.4"
														strokeLinecap="round"
														strokeLinejoin="round">
														<line
															x1="5"
															y1="12"
															x2="19"
															y2="12"
														/>
														<polyline points="12 5 19 12 12 19" />
													</svg>
												</span>
											</div>
										)}
									</div>

									{/* ===== BORDER GLOW ===== */}
									<div className="absolute inset-0 rounded-2xl border border-white/10 group-hover:border-[#f9a93a]/40 transition duration-500" />
								</motion.div>
							);
						})}
					</div>
				</div>
				{/* ================= FULLSCREEN TRUST SECTION ================= */}
				<RevealOnScroll delay={0.2}>
					<section className="relative w-full my-10 mt-30 py-10 min-h-[80vh] flex flex-col justify-center overflow-hidden bg-[#060010]">
						{/* ===== BACKGROUND GLOW SYSTEM ===== */}
						<div className="pointer-events-none absolute inset-0">
							{/* center glow */}
							<div className="absolute top-1/2 left-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#2d2f92]/20 blur-[120px]" />

							{/* gold accent */}
							<div className="absolute top-[20%] right-[10%] h-[300px] w-[300px] rounded-full bg-[#f9a93a]/20 blur-[100px]" />

							{/* subtle grid */}
							<div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(255,255,255,0.15)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.15)_1px,transparent_1px)] [background-size:60px_60px]" />
						</div>

						{/* ===== CONTENT WRAPPER ===== */}
						<div className="relative z-10 flex flex-col items-center justify-center px-6">
							<PageHeader
								eyebrow={t("aboutPages.ourStory.trustedHeader.eyebrow")}
								title={t("aboutPages.ourStory.trustedHeader.title")}
								description={t("aboutPages.ourStory.trustedHeader.description")}
								align="center"
								className="mx-auto text-center items-center text-white"
							/>

							{/* ===== FLOWING MENU FULL WIDTH ===== */}
							<div className="relative w-full mt-10">
								{/* side fade */}
								<div className="pointer-events-none absolute left-0 top-0 h-full w-32 bg-gradient-to-r from-[#060010] to-transparent z-10" />
								<div className="pointer-events-none absolute right-0 top-0 h-full w-32 bg-gradient-to-l from-[#060010] to-transparent z-10" />

								<div className="h-160 flex items-center">
									<FlowingMenu
										items={trustedBy}
										speed={20}
										textColor="#ffffff"
										bgColor="transparent"
										marqueeBgColor="#f9a93a"
										marqueeTextColor="#060010"
										borderColor="rgba(255,255,255,0.2)"
									/>
								</div>
							</div>
						</div>
					</section>
				</RevealOnScroll>
			</SectionWrapper>

			<SectionDivider />

			<SectionWrapper className="pt-0 ">
				<PageHeader
					eyebrow={t("aboutPages.ourStory.standardsHeader.eyebrow")}
					title={t("aboutPages.ourStory.standardsHeader.title")}
					description={t("aboutPages.ourStory.standardsHeader.description")}
					align="center"
					className="mx-auto text-center items-center"
				/>

				{/* ===== STANDARDS CARDS â€” image-free, data-forward ===== */}
				<div className="relative mt-16 px-6">
					{/* Ambient background tint */}
					<div
						className="pointer-events-none absolute -top-20 left-1/2 h-72 w-[60%] -translate-x-1/2 rounded-full blur-3xl"
						style={{
							background:
								"radial-gradient(ellipse, rgba(59,0,146,0.06), transparent 70%)",
						}}
					/>

					<div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
						{standards.map((section, index) => {
							const STANDARD_ICONS = [Award, Globe2, Layers3];
							const Icon = STANDARD_ICONS[index] ?? ShieldCheck;
							const theme = STANDARD_THEMES[index] ?? STANDARD_THEMES[0];

							return (
								<motion.div
									key={section.title}
									initial={{ opacity: 0, y: 40 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: true, margin: "-50px" }}
									whileHover={{ y: -6 }}
									transition={{
										duration: 0.55,
										delay: index * 0.1,
										ease: [0.22, 1, 0.36, 1],
									}}
									className="group relative h-full pt-14">
									{/* Floating icon â€” centered, half above the card */}
									<div
										className={`absolute left-1/2 top-14 z-20 flex h-28 w-28 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-2xl ${theme.iconShadow} transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3`}
										style={{ background: theme.iconBg }}>
										<Icon className="h-12 w-12 text-white" />
									</div>

									{/* Card body */}
									<div
										className={`relative h-full min-h-120 overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-b from-white via-white to-slate-50/60 px-8 pt-24 pb-10 shadow-[0_12px_30px_rgba(15,23,42,0.08)] backdrop-blur-sm transition-all duration-500 group-hover:border-transparent ${theme.cardBorderHover} ${theme.cardShadowHover}`}>
										{/* Top-right gradient corner accent */}
										<div
											className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full opacity-60 transition-all duration-500 group-hover:opacity-100"
											style={{
												background: `radial-gradient(circle, ${theme.cornerGlow}, transparent 70%)`,
											}}
										/>

										{/* Ghost icon in background */}
										<Icon
											className={`pointer-events-none absolute -right-8 -bottom-8 h-56 w-56 transition-all duration-500 group-hover:rotate-6 ${theme.ghostText}`}
											strokeWidth={1.25}
										/>

										{/* Title */}
										<div className="relative text-center">
											<h3 className="text-[1.35rem] font-semibold leading-snug tracking-tight text-slate-900">
												{section.title}
											</h3>
											<div
												className="mx-auto mt-3 h-[3px] w-14 rounded-full"
												style={{ background: theme.sweepBar }}
											/>
										</div>

										{/* Items â€” parsed into label + body, each with a relevant icon */}
										<div className="relative mt-6 space-y-2">
											{section.items.map((item, i) => {
												const label = item.code;
												const body = item.description;
												const meta = getItemMeta(`${label} ${body}`);
												const ItemIcon = meta.Icon;
												return (
													<div
														key={i}
														className="group/item flex items-start gap-4 rounded-xl border border-transparent p-2.5 transition-all duration-300 hover:border-slate-200/80 hover:bg-white hover:shadow-[0_4px_14px_rgba(15,23,42,0.06)]">
														<div
															className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl ${meta.image ? "border border-slate-200 bg-white" : `ring-1 ${meta.bg} ${meta.ring}`} transition-transform duration-300 group-hover/item:scale-105`}>
															{meta.image ?
																<img
																	src={meta.image}
																	alt={label || "ISO certification"}
																	className="h-12 w-12 object-contain"
																/>
															:	<ItemIcon
																	className={`h-6 w-6 ${meta.fg}`}
																	strokeWidth={2}
																/>
															}
														</div>
														<div className="flex-1 pt-0.5">
															{label && (
																<p className="text-sm font-semibold leading-snug text-slate-900">
																	{label}
																</p>
															)}
															<p
																className={`text-xs leading-relaxed text-slate-600 ${
																	label ? "mt-1" : ""
																}`}>
																{body}
															</p>
														</div>
													</div>
												);
											})}
										</div>

										{/* Bottom gradient bar â€” sweeps in on hover */}
										<div
											className="pointer-events-none absolute bottom-0 left-0 h-1 w-0 transition-all duration-500 group-hover:w-full"
											style={{ background: theme.sweepBar }}
										/>
									</div>
								</motion.div>
							);
						})}
					</div>
				</div>
			</SectionWrapper>

			<SectionDivider />

			<FooterSection />
		</PageShell>
	);
};

export default OurStory;
